import { useEffect, useRef } from 'react'
import classNames from 'classnames'

import { useScreen } from '../../../hooks'
import { AI_TEXT_GENERATION_ADAPTIVE_SCREEN_BREAKPOINTS } from '../AITextGeneration.constants'
import { useAITextGenerationFormFieldsState } from '../AITextGeneration.hooks'

import { AITextGenerationFormFieldsSettings } from './AITextGenerationFormFieldsSettings'
import { AITextGenerationFormFieldsSkeleton } from './AITextGenerationFormFieldsSkeleton'
import { AITextGenerationFormFieldsTopRow } from './AITextGenerationFormFieldsTopRow'

import * as styles from './AITextGenerationFormFields.module.css'

function AITextGenerationFormFields({
  disabled,
  errorMessage,
  hasFieldContent,
  hasGeneratedOnce,
  isGenerating,
  isTemplatesLoading,
  lastSubmittedSettings,
  lengthOptions,
  regenerateRef,
  templates,
  toneOptions,
}) {
  const fieldsRef = useRef()

  const { containerWidth } = useScreen({
    containerRef: fieldsRef,
  })

  const isMobile =
    containerWidth !== null &&
    containerWidth < AI_TEXT_GENERATION_ADAPTIVE_SCREEN_BREAKPOINTS[501]
  const isMobileCompact =
    containerWidth !== null &&
    containerWidth < AI_TEXT_GENERATION_ADAPTIVE_SCREEN_BREAKPOINTS[401]

  const {
    combinedErrorMessage,
    generateButtonText,
    isGenerateDisabled,
    isInteractionDisabled,
    isPromptLimitReached,
    submitForm,
    voice,
  } = useAITextGenerationFormFieldsState({
    disabled,
    errorMessage,
    hasFieldContent,
    hasGeneratedOnce,
    isGenerating,
    lastSubmittedSettings,
  })

  useEffect(() => {
    if (regenerateRef) {
      // Clear the ref while disabled so the Draft Panel "Regenerate" action
      // becomes a no-op instead of bypassing the disabled state.
      regenerateRef.current = disabled ? null : submitForm
    }
  }, [disabled, regenerateRef, submitForm])

  const fieldClassName = classNames(styles.field, {
    [styles.fieldError]: Boolean(combinedErrorMessage),
  })

  return (
    <>
      <div className={fieldClassName} ref={fieldsRef}>
        {isTemplatesLoading ? (
          <AITextGenerationFormFieldsSkeleton />
        ) : (
          <>
            <AITextGenerationFormFieldsTopRow
              disabled={disabled}
              generateButtonText={generateButtonText}
              hasGeneratedOnce={hasGeneratedOnce}
              isGenerateDisabled={isGenerateDisabled}
              isGenerating={isGenerating}
              isInteractionDisabled={isInteractionDisabled}
              isMobile={isMobile}
              isPromptLimitReached={isPromptLimitReached}
              voice={voice}
            />
            <AITextGenerationFormFieldsSettings
              isInteractionDisabled={isInteractionDisabled}
              isMobileCompact={isMobileCompact}
              lengthOptions={lengthOptions}
              templates={templates}
              toneOptions={toneOptions}
            />
          </>
        )}
      </div>

      {combinedErrorMessage ? (
        <div className={styles.errorText}>{combinedErrorMessage}</div>
      ) : null}
    </>
  )
}

export default AITextGenerationFormFields
